import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import EnhancedDatabaseAdmin from './EnhancedDatabaseAdmin';
import DocumentManagement from './DocumentManagement';
import { 
  Users, 
  FileText, 
  FileCheck,
  TrendingUp,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  Settings,
  Shield,
  AlertTriangle,
  Wrench,
  Building2,
  Database,
  X,
  Download
} from 'lucide-react';

const AdminDashboard = () => {
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [adminUser, setAdminUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [reAgents, setReAgents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [showEnhancedAdmin, setShowEnhancedAdmin] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (adminToken) {
      const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
      setAdminUser(user);
      loadDashboardData();
    }
  }, [adminToken]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load stats
      console.log('🔍 Loading dashboard stats...');
      const statsResponse = await fetch(`${backendUrl}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📊 Stats response status:', statsResponse.status);
      if (!statsResponse.ok) {
        const statsError = await statsResponse.text();
        console.error('❌ Stats API failed:', statsResponse.status, statsError);
        throw new Error(`Stats API failed: ${statsResponse.status} - ${statsError}`);
      }
      const statsData = await statsResponse.json();

      // Load subscription health
      console.log('📡 Loading subscription health...');
      const subsResponse = await fetch(`${backendUrl}/api/admin/subscriptions/health`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('📡 Subscription health status:', subsResponse.status);
      let subsData = null;
      if (subsResponse.ok) {
        subsData = await subsResponse.json();
      }

      const combinedStats = {
        ...statsData,
        subscription_health: subsData,
      };
      console.log('📊 Combined stats data:', combinedStats);
      setStats(combinedStats);

      // Load members (use higher limit to ensure we get all contractors)
      console.log('👥 Loading members...');
      const membersResponse = await fetch(`${backendUrl}/api/admin/members/search?limit=500`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('👥 Members response status:', membersResponse.status);
      let membersData = [];
      if (membersResponse.ok) {
        membersData = await membersResponse.json();
        console.log('👥 Members data count:', Array.isArray(membersData) ? membersData.length : 'Not array');
        setMembers(membersData);
      } else {
        const membersError = await membersResponse.text();
        console.error('❌ Members API failed:', membersResponse.status, membersError);
        throw new Error(`Members API failed: ${membersResponse.status} - ${membersError}`);
      }

      // Load applications (use higher limit to ensure we get all contractors)
      console.log('📝 Loading applications...');
      const appsResponse = await fetch(`${backendUrl}/api/admin/applications/search?limit=500`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📝 Applications response status:', appsResponse.status);
      let appsData = [];
      if (appsResponse.ok) {
        appsData = await appsResponse.json();
        console.log('📝 Applications data count:', Array.isArray(appsData) ? appsData.length : 'Not array');
        setApplications(appsData);
      } else {
        const appsError = await appsResponse.text();
        console.error('❌ Applications API failed:', appsResponse.status, appsError);
        throw new Error(`Applications API failed: ${appsResponse.status} - ${appsError}`);
      }

      // Load recent contracts
      const contractsResponse = await fetch(`${backendUrl}/api/admin/contracts?limit=20`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (contractsResponse.ok) {
        const contractsData = await contractsResponse.json();
        setContracts(contractsData.contracts || []);
      }

      // Load recent payments
      const paymentsResponse = await fetch(`${backendUrl}/api/admin/payments?limit=50`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        setPayments(paymentsData.payments || []);
      }

      // Load contractors using dedicated endpoint
      const contractorsResponse = await fetch(`${backendUrl}/api/admin/contractors`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (contractorsResponse.ok) {
        const contractorsData = await contractorsResponse.json();
        // Combine contractor users and contractor applications
        const allContractors = [
          ...(contractorsData.contractors || []), 
          ...(contractorsData.contractor_applications || [])
        ];
        setContractors(allContractors);
      } else {
        // Fallback to manual filtering if contractors endpoint fails
        const contractorsFromMembers = membersData ? membersData.filter(member => 
          member.property_management_status && member.property_management_status.toLowerCase() === 'contractor') : [];
        const contractorsFromApps = appsData ? appsData.filter(app => 
          app.property_management_status && app.property_management_status.toLowerCase() === 'contractor') : [];
        
        const allContractors = [...contractorsFromMembers, ...contractorsFromApps];
        setContractors(allContractors);
      }

      // Load RE Agents
      const reAgentsResponse = await fetch(`${backendUrl}/api/re-agents/admin/agents`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (reAgentsResponse.ok) {
        const reAgentsData = await reAgentsResponse.json();
        setReAgents(reAgentsData.agents || []);
      }

      // Load documents
      const documentsResponse = await fetch(`${backendUrl}/api/documents/admin/list?limit=50`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (documentsResponse.ok) {
        const documentsData = await documentsResponse.json();
        setDocuments(documentsData.documents || []);
      }

    } catch (error) {
      console.error('Dashboard data loading error:', error);
      
      // Show detailed error information
      const errorMessage = `Error loading dashboard data: ${error.message}
      
Debug Info:
- Backend URL: ${backendUrl}
- Admin Token: ${adminToken ? 'Present' : 'Missing'}
- Error: ${error.message}

This error indicates the admin dashboard cannot fetch data from the backend.`;

      alert(errorMessage);
      
      // Also log to console for debugging
      console.log('Debug Info:', {
        backendUrl,
        hasToken: !!adminToken,
        tokenLength: adminToken ? adminToken.length : 0,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMemberAction = async (memberId, action) => {
    try {
      let url = `${backendUrl}/api/admin/members/${memberId}`;
      let method = 'PUT';
      let body = null;

      if (action === 'delete') {
        // Use our new individual delete function
        const member = members.find(m => m.id === memberId);
        await deleteMember(memberId, member?.email || memberId);
        return;
      } else if (action === 'deactivate') {
        body = JSON.stringify({ is_active: false });
      } else if (action === 'activate') {
        body = JSON.stringify({ is_active: true });
      } else if (action === 'reset_password') {
        url = `${backendUrl}/api/admin/members/${memberId}/reset-password`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body
      });

      if (response.ok) {
        const result = await response.json();
        
        if (action === 'reset_password' && result.temporary_password) {
          alert(`Password reset successfully!\nTemporary password: ${result.temporary_password}\nPlease provide this to the member securely.`);
        } else {
          alert('Action completed successfully!');
        }
        
        loadDashboardData(); // Reload data
      } else {
        throw new Error('Action failed');
      }
    } catch (error) {
      console.error('Error performing action:', error);
      alert('Action failed. Please try again.');
    }
  };

  // Delete functions
  const deleteMember = async (memberId, memberEmail) => {
    if (!confirm(`Are you sure you want to delete member: ${memberEmail}?\n\nThis action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [`member-${memberId}`]: true }));

    try {
      const response = await fetch(`${backendUrl}/api/admin/member/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Member deleted successfully!');
        await loadDashboardData(); // Refresh the data
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete member error:', error);
      alert(`Failed to delete member: ${error.message}`);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [`member-${memberId}`]: false }));
    }
  };

  const deleteApplication = async (applicationId, applicationEmail) => {
    if (!confirm(`Are you sure you want to delete application: ${applicationEmail}?\n\nThis will also delete any related contracts.\nThis action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [`application-${applicationId}`]: true }));

    try {
      const response = await fetch(`${backendUrl}/api/admin/application/${applicationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Application deleted successfully!');
        await loadDashboardData(); // Refresh the data
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete application error:', error);
      alert(`Failed to delete application: ${error.message}`);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [`application-${applicationId}`]: false }));
    }
  };

  const deleteContract = async (contractId, contractEmail) => {
    if (!confirm(`Are you sure you want to delete contract: ${contractEmail}?\n\nThis action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [`contract-${contractId}`]: true }));

    try {
      const response = await fetch(`${backendUrl}/api/admin/contract/${contractId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Contract deleted successfully!');
        await loadDashboardData(); // Refresh the data
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete contract error:', error);
      alert(`Failed to delete contract: ${error.message}`);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [`contract-${contractId}`]: false }));
    }
  };

  const deletePayment = async (paymentId, paymentAmount) => {
    if (!confirm(`Are you sure you want to delete payment record: $${paymentAmount}?\n\nThis action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [`payment-${paymentId}`]: true }));

    try {
      const response = await fetch(`${backendUrl}/api/admin/payment/${paymentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Payment deleted successfully!');
        await loadDashboardData(); // Refresh the data
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete payment error:', error);
      alert(`Failed to delete payment: ${error.message}`);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [`payment-${paymentId}`]: false }));
    }
  };

  const searchMembers = async () => {
    if (!searchTerm) {
      loadDashboardData();
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/members/search?email=${encodeURIComponent(searchTerm)}&name=${encodeURIComponent(searchTerm)}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error searching members:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setAdminToken(null);
    setAdminUser(null);
  };

  if (!adminToken) {
    return <AdminLogin onLogin={(token, user) => {
      setAdminToken(token);
      setAdminUser(user);
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
    }} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Legacy Property Association Admin
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Welcome, {adminUser?.full_name}
              </span>
              <Button variant="outline" size="sm" onClick={logout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
              { id: 'members', name: 'Members', icon: Users },
              { id: 'contractors', name: 'Contractors', icon: Wrench },
              { id: 're-agents', name: 'RE Agents', icon: Building2 },
              { id: 'applications', name: 'Applications', icon: FileText },
              { id: 'contracts', name: 'Contracts', icon: FileCheck },
              { id: 'payments', name: 'Payments', icon: Settings },
              { id: 'documents', name: 'Documents', icon: FileText }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
              <div className="flex space-x-2">
                <Button 
                  onClick={() => setShowEnhancedAdmin(true)}
                  variant="outline"
                  className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                >
                  <Database className="h-4 w-4 mr-2" />
                  Enhanced Database Admin
                </Button>
                <Button onClick={loadDashboardData} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total_members}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.active_members} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Applications</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total_applications}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.pending_applications} pending
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Membership Levels</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      <div className="text-sm">Basic: {stats.basic_members}</div>
                      <div className="text-sm">Standard: {stats.standard_members}</div>
                      <div className="text-sm">Premium: {stats.premium_members}</div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Recent Signups</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.recent_signups}</div>
                    <p className="text-xs text-muted-foreground">Last 30 days</p>
                  </CardContent>
                </Card>

            {/* Subscription Health Summary */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Subscription Health</span>
                  <span className="text-xs text-gray-500">Standard & Premium members only</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Paid Members</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.subscription_health?.total_paid_members ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active</p>
                    <p className="text-2xl font-bold text-green-600">{stats.subscription_health?.active_subscriptions ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Past Due (Grace)</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.subscription_health?.in_grace_period ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Needs Attention</p>
                    <p className="text-2xl font-bold text-red-600">{stats.subscription_health?.past_grace_period ?? 0}</p>
                  </div>
                </div>
                {stats.subscription_health?.members_needing_attention?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-red-700 mb-2">Members Needing Attention</p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-red-800">Member</th>
                            <th className="px-3 py-2 text-left font-medium text-red-800">Level</th>
                            <th className="px-3 py-2 text-left font-medium text-red-800">Failed Payments</th>
                            <th className="px-3 py-2 text-left font-medium text-red-800">Grace Ended</th>
                            <th className="px-3 py-2 text-left font-medium text-red-800">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {stats.subscription_health.members_needing_attention.map((m, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{m.name}</div>
                                <div className="text-xs text-gray-500">{m.email}</div>
                              </td>
                              <td className="px-3 py-2">{m.membership_level}</td>
                              <td className="px-3 py-2">{m.failed_payments}</td>
                              <td className="px-3 py-2">{m.grace_period_ended ? new Date(m.grace_period_ended).toLocaleDateString() : '-'}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{m.action}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

              </div>
            )}
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Members Management</h2>
              <div className="flex space-x-2">
                <div className="flex">
                  <Input
                    type="text"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="rounded-r-none"
                  />
                  <Button onClick={searchMembers} className="rounded-l-none">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                <Button onClick={loadDashboardData} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Members ({members.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Member
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Membership
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contractor Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {members.map((member) => (
                        <tr key={member.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {member.full_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {member.email}
                              </div>
                              <div className="text-sm text-gray-500">
                                {member.phone}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              member.membership_level === 'Basic' ? 'bg-green-100 text-green-800' :
                              member.membership_level === 'Standard' ? 'bg-amber-100 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {member.membership_level}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {member.property_management_status === 'contractor' ? (
                              <div className="text-sm">
                                <div className="flex items-center mb-1">
                                  <Badge className="bg-orange-100 text-orange-800 mr-2">
                                    🔧 Contractor
                                  </Badge>
                                </div>
                                {member.business_name && (
                                  <div className="text-xs text-gray-600 mb-1">
                                    <strong>Business:</strong> {member.business_name}
                                  </div>
                                )}
                                {member.license_status && (
                                  <div className="text-xs text-gray-600 mb-1">
                                    <strong>License:</strong> {member.license_status}
                                  </div>
                                )}
                                {member.specialty_skills && member.specialty_skills.length > 0 && (
                                  <div className="text-xs text-gray-600">
                                    <strong>Skills:</strong> {member.specialty_skills.join(', ')}
                                  </div>
                                )}
                                {member.other_specialty && (
                                  <div className="text-xs text-gray-600">
                                    <strong>Other:</strong> {member.other_specialty}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600">
                                Property Owner
                              </Badge>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {member.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(member.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMemberAction(member.id, 'reset_password')}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Reset Password
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMemberAction(member.id, member.is_active ? 'deactivate' : 'activate')}
                            >
                              {member.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleMemberAction(member.id, 'delete')}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contractors Tab */}
        {activeTab === 'contractors' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex space-x-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Simple CSV export of contractors
                    const headers = [
                      'Full Name',
                      'Email',
                      'Phone',
                      'Business Name',
                      'License Status',
                      'Specialty Skills',
                      'Other Specialty',
                      'Membership Level',
                      'Status',
                      'Source'
                    ];

                    const rows = contractors.map(c => {
                      const fullName = c.first_name ? `${c.first_name} ${c.last_name || ''}`.trim() : (c.full_name || '');
                      const skills = Array.isArray(c.specialty_skills) ? c.specialty_skills.join('; ') : '';
                      const status = c.status || (c.is_active ? 'Active' : 'Inactive');
                      const source = c.application_id ? 'Application' : 'Member';

                      return [
                        fullName,
                        c.email || '',
                        c.phone || '',
                        c.business_name || '',
                        c.license_status || '',
                        skills,
                        c.other_specialty || '',
                        c.membership_level || '',
                        status,
                        source
                      ];
                    });

                    const csvContent = [headers, ...rows]
                      .map(row => row
                        .map(value => {
                          if (value == null) return '';
                          const str = String(value);
                          // Escape quotes and wrap fields containing commas/newlines
                          const safe = str.replace(/"/g, '""');
                          return /[",\n]/.test(safe) ? `"${safe}"` : safe;
                        })
                        .join(',')
                      )
                      .join('\n');

                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    const timestamp = new Date().toISOString().slice(0,10);
                    link.href = url;
                    link.setAttribute('download', `lpa-contractors-${timestamp}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
                <Button onClick={loadDashboardData} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              <h2 className="text-2xl font-bold text-gray-900">🔧 Contractor Directory</h2>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>All Contractors ({contractors.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contractor Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          License Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Specialty Skills
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Membership
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contractors.map((contractor) => (
                        <tr key={contractor.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {contractor.first_name ? `${contractor.first_name} ${contractor.last_name}` : contractor.full_name}
                              </div>
                              {contractor.business_name && (
                                <div className="text-xs text-gray-600 mb-1">
                                  🏢 {contractor.business_name}
                                </div>
                              )}
                              <div className="text-xs text-gray-500">
                                ID: {contractor.id}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="text-gray-900">{contractor.email}</div>
                              <div className="text-gray-500">{contractor.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              contractor.license_status === 'Licensed' ? 'bg-green-100 text-green-800' : 
                              contractor.license_status === 'Non-licensed' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {contractor.license_status || 'Not Specified'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              {contractor.specialty_skills && contractor.specialty_skills.length > 0 ? (
                                <div>
                                  {contractor.specialty_skills.map((skill, index) => (
                                    <Badge key={index} className="bg-blue-100 text-blue-800 mr-1 mb-1">
                                      {skill}
                                    </Badge>
                                  ))}
                                  {contractor.other_specialty && (
                                    <Badge className="bg-purple-100 text-purple-800 mr-1 mb-1">
                                      {contractor.other_specialty}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">No skills specified</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              contractor.membership_level === 'Basic' ? 'bg-green-100 text-green-800' :
                              contractor.membership_level === 'Standard' ? 'bg-amber-100 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {contractor.membership_level}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              contractor.status === 'active' || contractor.is_active ? 'bg-green-100 text-green-800' :
                              contractor.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {contractor.status || (contractor.is_active ? 'Active' : 'Inactive')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contractors.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No contractors found in the system
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}


        {/* Real Estate Agents Tab */}
        {activeTab === 're-agents' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">🏢 Real Estate Agents</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {reAgents.length} of 5 maximum agents | Exclusive network for members
                </p>
              </div>
              <div className="flex space-x-2">
                <Button onClick={loadDashboardData} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button 
                  onClick={() => alert('Add Agent form coming in next update')}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={reAgents.length >= 5}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Add Agent {reAgents.length >= 5 && '(Limit Reached)'}
                </Button>
              </div>
            </div>

            {/* Agent Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {reAgents.filter(a => a.is_active).length}
                  </div>
                  <div className="text-sm text-gray-600">Active Agents</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {reAgents.reduce((sum, a) => sum + (a.total_contacts || 0), 0)}
                  </div>
                  <div className="text-sm text-gray-600">Total Contacts</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-600">
                    {reAgents.reduce((sum, a) => sum + (a.total_conversions || 0), 0)}
                  </div>
                  <div className="text-sm text-gray-600">Deals Closed</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-amber-600">
                    {5 - reAgents.length}
                  </div>
                  <div className="text-sm text-gray-600">Slots Available</div>
                </CardContent>
              </Card>
            </div>

            {/* Agents Table */}
            <Card>
              <CardHeader>
                <CardTitle>Agent Directory ({reAgents.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Agent Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Specialties
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Performance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rating
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reAgents.map((agent) => (
                        <tr key={agent.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {agent.first_name} {agent.last_name}
                              </div>
                              {agent.license_number && (
                                <div className="text-xs text-gray-500">
                                  License: {agent.license_number}
                                </div>
                              )}
                              <div className="text-xs text-gray-500">
                                ID: {agent.id.substring(0, 8)}...
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {agent.company_name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="text-gray-900">{agent.email}</div>
                              <div className="text-gray-500">{agent.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              {agent.specialties && agent.specialties.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {agent.specialties.map((specialty, index) => (
                                    <Badge key={index} className="bg-blue-100 text-blue-800">
                                      {specialty}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">No specialties</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="text-gray-900">
                                {agent.total_contacts || 0} contacts
                              </div>
                              <div className="text-green-600 font-medium">
                                {agent.total_conversions || 0} deals
                              </div>
                              {agent.total_contacts > 0 && (
                                <div className="text-xs text-gray-500">
                                  {Math.round(((agent.total_conversions || 0) / agent.total_contacts) * 100)}% rate
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="flex items-center">
                                <span className="text-yellow-500">★</span>
                                <span className="ml-1 text-gray-900 font-medium">
                                  {agent.average_rating ? agent.average_rating.toFixed(1) : '0.0'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {agent.total_reviews || 0} reviews
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              agent.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }>
                              {agent.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reAgents.length === 0 && (
                    <div className="text-center py-12">
                      <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">No real estate agents added yet</p>
                      <p className="text-gray-400 text-sm mb-4">
                        Add up to 5 exclusive agents to your network
                      </p>
                      <Button 
                        onClick={() => alert('Add Agent form coming soon')}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Add Your First Agent
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Applications</h2>
              <Button onClick={loadDashboardData} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Applications ({applications.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Applicant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Membership
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contractor Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Applied
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {app.first_name} {app.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {app.email}
                              </div>
                              <div className="text-sm text-gray-500">
                                {app.phone}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              app.membership_level === 'Basic' ? 'bg-green-100 text-green-800' :
                              app.membership_level === 'Standard' ? 'bg-amber-100 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {app.membership_level}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {app.property_management_status === 'contractor' ? (
                              <div className="text-sm">
                                <div className="flex items-center mb-1">
                                  <Badge className="bg-orange-100 text-orange-800 mr-2">
                                    🔧 Contractor
                                  </Badge>
                                </div>
                                {app.business_name && (
                                  <div className="text-xs text-gray-600 mb-1">
                                    <strong>Business:</strong> {app.business_name}
                                  </div>
                                )}
                                {app.license_status && (
                                  <div className="text-xs text-gray-600 mb-1">
                                    <strong>License:</strong> {app.license_status}
                                  </div>
                                )}
                                {app.specialty_skills && app.specialty_skills.length > 0 && (
                                  <div className="text-xs text-gray-600">
                                    <strong>Skills:</strong> {app.specialty_skills.join(', ')}
                                  </div>
                                )}
                                {app.other_specialty && (
                                  <div className="text-xs text-gray-600">
                                    <strong>Other:</strong> {app.other_specialty}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600">
                                Property Owner
                              </Badge>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              app.status === 'active' ? 'bg-green-100 text-green-800' :
                              app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              app.status === 'contracts_sent' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {app.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={app.has_user_account ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {app.has_user_account ? 'Created' : 'Not Created'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(app.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteApplication(app.id, app.email)}
                              disabled={deleteLoading[`application-${app.id}`]}
                            >
                              {deleteLoading[`application-${app.id}`] ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contracts Tab */}
        {activeTab === 'contracts' && (
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Digital Contracts</CardTitle>
                <Button onClick={loadDashboardData} disabled={loading}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Member
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Membership Level
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Signed Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contract Version
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email Sent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contracts.map((contract) => (
                        <tr key={contract.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {contract.member_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {contract.member_email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              contract.membership_level === 'PREMIUM' 
                                ? 'bg-blue-100 text-blue-800'
                                : contract.membership_level === 'STANDARD'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-green-100 text-green-800'
                            }>
                              {contract.membership_level}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(contract.signed_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {contract.contract_version}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={contract.email_sent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {contract.email_sent ? 'Sent' : 'Pending'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    // Fetch contract details from backend
                                    const response = await fetch(`${backendUrl}/api/admin/contracts/${contract.id}`, {
                                      headers: {
                                        'Authorization': `Bearer ${adminToken}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });
                                    
                                    if (response.ok) {
                                      const contractData = await response.json();
                                      
                                      // Create and show modal with contract details
                                      const contractText = contractData.contract_text || 'Contract text not available';
                                      const memberInfo = `Member: ${contractData.member_name}\\nEmail: ${contractData.member_email}\\nMembership: ${contractData.membership_level}\\nSigned: ${new Date(contractData.signed_at).toLocaleString()}\\n\\n`;
                                      
                                      // Show contract in alert (you can enhance this with a proper modal)
                                      alert(memberInfo + contractText.substring(0, 1000) + (contractText.length > 1000 ? '...\\n\\n[Contract text truncated - Full text available in database]' : ''));
                                    } else {
                                      alert('Failed to load contract details');
                                    }
                                  } catch (error) {
                                    console.error('Error viewing contract:', error);
                                    alert('Error loading contract: ' + error.message);
                                  }
                                }}
                              >
                                <FileCheck className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {!contract.email_sent && (
                                <Button 
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await fetch(`${backendUrl}/api/admin/contracts/${contract.id}/resend-email`, {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${adminToken}`,
                                          'Content-Type': 'application/json'
                                        }
                                      });
                                      loadDashboardData(); // Refresh data
                                    } catch (error) {
                                      console.error('Error resending email:', error);
                                    }
                                  }}
                                >
                                  Resend Email
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteContract(contract.id, contract.member_email)}
                                disabled={deleteLoading[`contract-${contract.id}`]}
                              >
                                {deleteLoading[`contract-${contract.id}`] ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contracts.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No signed contracts found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Payment Management</h2>
              <Button onClick={loadDashboardData} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Stripe Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <span>Stripe Integration Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="font-medium text-green-800">Live Stripe API Active</p>
                      <p className="text-sm text-green-600">Processing real payments with your live Stripe account</p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">LIVE MODE</Badge>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p><strong>Payment Flow:</strong> Member selects Standard/Premium → Signs contract → Payment via Stripe → Redirect to https://lpassociation.org</p>
                </div>
              </CardContent>
            </Card>

            {/* Payment Transactions */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Payment Transactions ({payments.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Member
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Membership
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Session ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {payment.member_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {payment.member_email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              payment.membership_level === 'Premium' ? 'bg-blue-100 text-blue-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {payment.membership_level}
                            </Badge>
                            <div className="text-xs text-gray-500 mt-1">
                              {payment.billing_frequency}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              ${payment.amount?.toFixed(2)}
                            </span>
                            <div className="text-xs text-gray-500">
                              {payment.currency?.toUpperCase()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={
                              payment.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                              payment.payment_status === 'initiated' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {payment.payment_status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {payment.session_id?.substring(0, 20)}...
                            </code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deletePayment(payment.id, payment.amount)}
                              disabled={deleteLoading[`payment-${payment.id}`]}
                            >
                              {deleteLoading[`payment-${payment.id}`] ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {payments.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No payment transactions found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <DocumentManagement 
            documents={documents}
            onDocumentUploaded={loadDashboardData}
            adminToken={adminToken}
            backendUrl={backendUrl}
          />
        )}
      </div>

      {/* Enhanced Database Admin Modal */}
      {showEnhancedAdmin && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking outside the content area
            if (e.target === e.currentTarget) {
              setShowEnhancedAdmin(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Database className="h-5 w-5 mr-2 text-purple-600" />
                Enhanced Database Admin
              </h2>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEnhancedAdmin(false);
                }}
                className="hover:bg-gray-100 z-10 relative"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              <EnhancedDatabaseAdmin 
                onClose={() => setShowEnhancedAdmin(false)} 
                adminToken={localStorage.getItem('adminToken')}
                onDataChanged={loadDashboardData}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${backendUrl}/api/auth/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(data.access_token, data.admin_user);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Login failed');
      }
    } catch (err) {
      setError('Login failed. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-600">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Legacy Property Association Administration
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Username or Email"
              value={credentials.username}
              onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              required
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;